// baseline-tester.js - Module 2, the pitch-side tester app (role_app=
// 'baseline_tester'). Open-station model: pick an exercise → see its untested
// queue → test a player with the right template → Save/Discard → next.
//
// Three templates (V6_DESIGN §7.3):
//   A stopwatch     - READY/START/STOP at 0.01 s, then Save/Discard.
//   B rep counter   - 40 s countdown, one giant tap button, no undo, haptic.
//   C gate scorer   - 3/1/0 buttons, undo, auto-stop at time or ball cap;
//                     dynamic distribution flips PUSH 1-4 → SHOT 1-4.
//
// Each test in the session also carries a RECORDING MODE (director 2026-10-30),
// chosen per test so one session can mix them:
//   'player'  the queue above: one time per child, tracked to her.
//   'anon'    real individual times attached to the TEAM, not to a child. Same
//             templates, no roster: record a time, record the next one. Holds
//             no personal data, so it is the one mode safe to compare between
//             clubs. Record ONE time per child, or the team's average reads
//             slower than an average of bests would.
//   'average' one team average typed straight in, with how many children it
//             covers so an age-group roll-up can weight it.
// Modes 'anon' and 'average' need the session to name a team, because an
// unattributed time has to belong to something and the session is the only
// place that says which team. They never reach a child's Dragons Score.
//
// Field resilience: saves go through a localStorage queue so patchy pitch-side
// connectivity never blocks testing; the queue flushes on a timer + on load.
// Testers never see historical results (RLS only returns this session).

import { AUTH } from './auth.js';
import { CONFIG } from './config.js';
import { DB } from './db.js';
import { FITNESS_TIMER } from './fitness-timer.js';
import { BASELINE_ILLUSTRATIONS } from './baseline-illustrations.js';
import { exerciseByKey, exercisesForKeys, formatAverage, formatValue, gateScore, testedCount, untestedQueue, registerCustomTests } from './baseline-data.js';
import { teamStatsForExercise } from './team-averages.js';
import { t } from './strings.js';
import { UI } from './ui.js';
import { UTILS } from './utils.js';

export const BASELINE_TESTER = (() => {

  let _exercises = [];   // [{ key, mode }] - mode is how this test is recorded
  let _session   = null; // the session row, for its team (anon/average modes)
  let _players   = [];   // [{id, name}]
  let _results   = [];   // this session's results (RLS-scoped)
  let _flushTimer = null;
  let _live = null;      // active test state (template-specific)

  // Tester context. Null = the classic BT- code login (read from AUTH). A
  // staff coach assigned as tester enters with an explicit ctx instead, so the
  // whole module runs identically off their own ST- login (director decision
  // 2026-07-02). Same session id + tester_code_id, just a different door.
  //   { sessionId, sessionName, testerName, testerCodeId, onExit }
  // A coach running their own team's test enters with testerCodeId null: on
  // that route the permission comes from the session being theirs, not a code.
  let _ctx = null;

  const _sid    = () => (_ctx ? _ctx.sessionId    : AUTH.getBaselineSessionId());
  const _sname  = () => (_ctx ? _ctx.sessionName  : AUTH.getBaselineSessionName());
  const _tname  = () => (_ctx ? _ctx.testerName   : AUTH.getTesterName());
  const _codeId = () => (_ctx ? _ctx.testerCodeId : AUTH.getBaselineTesterCodeId());
  const _active = () => !!_ctx || AUTH.isBaselineTester();

  const _pendingKey = () => `bl_pending_${_sid() || 'none'}`;

  const _modeOf = (key) => _exercises.find(e => e.key === key)?.mode || 'player';
  const _teamId = () => _ctx?.teamId || _session?.team_id || null;

  // Rows for one exercise in one mode. Without the mode filter a team row
  // would land in the per-child queue counts as a subject of its own (its
  // player_id is null, and a Set counts that null once).
  const _rowsFor = (key, mode) =>
    _results.filter(r => r.exercise_key === key && (r.record_mode || 'player') === mode);

  // ── Load + flush ─────────────────────────────────────────────────────────────

  async function showHome(ctx) {
    if (ctx) _ctx = ctx;   // staff-coach entry; omit to reuse the current door
    UI.showScreen('screen-baseline-tester');
    document.getElementById('baseline-tester-title').textContent = _sname() || t('bl.tester.title');
    document.getElementById('baseline-tester-name').textContent = t('bl.tester.welcome', { name: _tname() || '' });
    // The exit control: sign out for a BT- helper, "back to hub" for a coach.
    document.getElementById('baseline-tester-signout').textContent =
      _ctx ? t('bl.tester.backToHub') : t('bl.tester.signOut');
    UI.setLoading('baseline-tester-content', t('common.loading'));
    try {
      const sid = _sid();
      const [exRows, plRows, results, externals, customTests, session] = await Promise.all([
        DB.baseline.listExercises(sid),
        DB.baseline.listPlayers(sid),
        DB.baseline.listResults(sid).catch(() => []),
        DB.baseline.listExternals(sid).catch(() => []),
        DB.physicalTests.listActive().catch(() => []),
        DB.baseline.getSession(sid).catch(() => null),
      ]);
      registerCustomTests(customTests || []);  // so custom test keys resolve pitch-side
      _session = session || null;
      _exercises = (exRows || []).map(r => ({
        key: r.exercise_key, mode: r.record_mode || 'player',
      }));
      // Player names come back through the tester-scoped RLS on players.
      const ids = (plRows || []).map(r => r.player_id);
      const players = ids.length
        ? await DB.players.getByIds(ids).catch(() => [])
        : [];
      const nameById = {};
      (players || []).forEach(p => { nameById[p.id] = p.name; });
      // Subjects = registered players + external/guest entries (guests carry the
      // external row id and an external flag so saves go to external_id).
      const playerSubjects = ids.map(id => ({ id, name: nameById[id] || '-', external: false }));
      const guestSubjects = (externals || []).map(e => ({ id: e.id, name: e.name, external: true }));
      _players = [...playerSubjects, ...guestSubjects]
        .sort((a, b) => a.name.localeCompare(b.name));
      _results = results || [];
      _startFlusher();
      await _flushPending();
      _renderHome();
    } catch (err) {
      console.error('Tester home load failed:', err);
      UI.setEmpty('baseline-tester-content', t('bl.tester.loadFailed'));
    }
  }

  function _renderHome() {
    const el = document.getElementById('baseline-tester-content');
    if (!el) return;
    const esc = UTILS.escapeHtml;
    const list = exercisesForKeys(_exercises.map(e => e.key));
    const pending = _loadPending().length;

    // The progress chip has to describe the mode it is counting, or "3 / 12"
    // on an unattributed test would read as "9 girls still to do" when there
    // is no roster at all.
    const chip = (key) => {
      const mode = _modeOf(key);
      if (mode === 'anon') return t('bl.mode.anonCount', { count: _rowsFor(key, 'anon').length });
      if (mode === 'average') {
        const rows = _rowsFor(key, 'average');
        return rows.length ? t('bl.mode.avgDone') : t('bl.mode.avgTodo');
      }
      return t('bl.progressLine', { done: testedCount(key, _rowsFor(key, 'player')), total: _players.length });
    };

    el.innerHTML = `
      ${pending ? `<div class="bl-pending-note">${esc(t('bl.tester.pending', { count: pending }))}</div>` : ''}
      <div class="sel-detail-section__title" style="margin-bottom:8px;">${esc(t('bl.tester.pickExercise'))}</div>
      ${list.map(e => `
        <button class="player-eval-card" data-ex="${esc(e.key)}">
          <div class="player-eval-card__main">
            <div class="player-eval-card__title">${esc(e.name)}</div>
            <div class="player-eval-card__sub">${esc(e.domain)} · ${esc(t('bl.mode.' + _modeOf(e.key)))}</div>
          </div>
          <span class="player-read-chip">${esc(chip(e.key))}</span>
        </button>`).join('')}
    `;
    el.querySelectorAll('[data-ex]').forEach(b =>
      b.addEventListener('click', () => showQueue(b.dataset.ex)));
  }

  // ── Player queue for one exercise ────────────────────────────────────────────

  let _queueExercise = null;
  let _skipped = new Set();   // players sent to the tail this visit

  function showQueue(exerciseKey) {
    if (exerciseKey !== _queueExercise) _skipped = new Set(); // skips are per-exercise
    _queueExercise = exerciseKey;
    UI.showScreen('screen-baseline-queue');
    _renderStation();
  }

  // One screen, three stations. Which one depends on how this test is recorded.
  function _renderStation() {
    const mode = _modeOf(_queueExercise);
    if (mode === 'anon') return _renderAnonStation();
    if (mode === 'average') return _renderAverageStation();
    return _renderQueue();
  }

  function _renderQueue() {
    const ex = exerciseByKey(_queueExercise);
    document.getElementById('baseline-queue-title').textContent = ex?.name || '';
    document.getElementById('baseline-queue-sub').textContent = _sname() || '';
    const el = document.getElementById('baseline-queue-content');
    if (!el) return;
    const esc = UTILS.escapeHtml;

    // Untested first (skipped ones sink to the tail), then a "done this session"
    // list with the recorded value + retest/delete.
    const mine = _rowsFor(_queueExercise, 'player');
    let queue = untestedQueue(_queueExercise, _players, mine);
    queue = [...queue.filter(p => !_skipped.has(p.id)), ...queue.filter(p => _skipped.has(p.id))];

    const doneRows = mine
      .map(r => {
        const p = _players.find(x => x.id === (r.player_id || r.external_id));
        return { r, name: p?.name || '-' };
      });

    el.innerHTML = `
      <div style="margin-bottom:6px;"><button class="bl-illu-link" id="bl-how-measured">${esc(t('blillu.howMeasured'))}</button></div>
      <div class="sel-detail-section__title" style="margin-bottom:8px;">${esc(t('bl.tester.toTest', { count: queue.length }))}</div>
      ${queue.length ? queue.map(p => `
        <div class="bl-queue-row">
          <button class="bl-queue-name" data-test="${esc(p.id)}">${esc(p.name)}</button>
          <button class="sel-roster-btn" data-skip="${esc(p.id)}">${esc(t('bl.tester.skip'))}</button>
        </div>`).join('') : `<div class="sel-detail-hint">${esc(t('bl.tester.allDone'))}</div>`}

      ${doneRows.length ? `
        <div class="sel-detail-section__title" style="margin:16px 0 8px;">${esc(t('bl.tester.savedAttempts'))}</div>
        ${doneRows.map(({ r, name }) => `
          <div class="bl-queue-row">
            <span class="bl-queue-done">${esc(name)} · <strong>${esc(formatValue(ex, r))}</strong>${r._pending ? ' ⏳' : ''}</span>
            <button class="sel-roster-btn" data-retest="${esc(r.player_id || r.external_id)}">${esc(t('bl.tester.retest'))}</button>
            ${r.id ? `<button class="sel-roster-btn sel-roster-btn--danger" data-del="${esc(r.id)}">🗑</button>` : ''}
          </div>`).join('')}` : ''}
    `;

    document.getElementById('bl-how-measured')
      ?.addEventListener('click', () => BASELINE_ILLUSTRATIONS.show(_queueExercise));
    el.querySelectorAll('[data-test]').forEach(b =>
      b.addEventListener('click', () => _startTest(b.dataset.test)));
    el.querySelectorAll('[data-retest]').forEach(b =>
      b.addEventListener('click', () => _startTest(b.dataset.retest)));
    el.querySelectorAll('[data-skip]').forEach(b =>
      b.addEventListener('click', () => { _skipped.add(b.dataset.skip); _renderQueue(); }));
    el.querySelectorAll('[data-del]').forEach(b =>
      b.addEventListener('click', () => _confirmDelete(b.dataset.del)));
  }

  // ── Station 2 - unattributed individual times (record_mode 'anon') ──────────
  //
  // No roster, so no queue: just "record the next time". The running mean, best
  // and spread come from the SAME pure module the director's averages screen
  // uses, so pitch-side and the office cannot disagree about a team.

  function _renderAnonStation() {
    const teamId = _teamId();
    if (!teamId) return _missingTeamNotice();
    const ex = exerciseByKey(_queueExercise);
    const esc = UTILS.escapeHtml;
    document.getElementById('baseline-queue-title').textContent = ex?.name || '';
    document.getElementById('baseline-queue-sub').textContent = `${teamId} · ${t('bl.mode.anon')}`;
    const el = document.getElementById('baseline-queue-content');
    if (!el) return;

    // In the order they were RUN, not the order the database hands them back,
    // so "Time 3" is the third girl who went. A row still in the offline queue
    // has no recorded_at yet and is the newest, so it sorts last.
    const rows = _rowsFor(_queueExercise, 'anon')
      .slice()
      .sort((a, b) => String(a.recorded_at || '9999').localeCompare(String(b.recorded_at || '9999')));
    const stats = teamStatsForExercise({
      exerciseKey: _queueExercise, exercise: ex, results: rows,
    }).get(teamId);

    el.innerHTML = `
      <div style="margin-bottom:6px;"><button class="bl-illu-link" id="bl-how-measured">${esc(t('blillu.howMeasured'))}</button></div>
      <div class="sel-detail-hint" style="margin-bottom:10px;">${esc(t('bl.mode.anonHint'))}</div>
      <button class="btn btn--primary bl-big" id="bl-anon-add">${esc(t('bl.mode.anonAdd'))}</button>
      ${stats ? `
        <div class="bl-team-stats">
          <span><strong>${esc(formatAverage(ex, stats.mean))}</strong> ${esc(t('bl.mode.statMean'))}</span>
          <span><strong>${esc(formatValue(ex, { value: stats.best }))}</strong> ${esc(t('bl.mode.statBest'))}</span>
          <span><strong>${stats.n}</strong> ${esc(t('bl.mode.statTimes'))}</span>
        </div>` : ''}
      ${rows.length ? `
        <div class="sel-detail-section__title" style="margin:16px 0 8px;">${esc(t('bl.tester.savedAttempts'))}</div>
        ${rows.map((r, i) => `
          <div class="bl-queue-row">
            <span class="bl-queue-done">${esc(t('bl.mode.anonNth', { n: i + 1 }))} · <strong>${esc(formatValue(ex, r))}</strong>${r._pending ? ' ⏳' : ''}</span>
            ${r.id ? `<button class="sel-roster-btn sel-roster-btn--danger" data-del="${esc(r.id)}">🗑</button>` : ''}
          </div>`).join('')}` : ''}
    `;

    document.getElementById('bl-how-measured')
      ?.addEventListener('click', () => BASELINE_ILLUSTRATIONS.show(_queueExercise));
    document.getElementById('bl-anon-add')?.addEventListener('click', _startAnonTest);
    el.querySelectorAll('[data-del]').forEach(b =>
      b.addEventListener('click', () => _confirmDelete(b.dataset.del)));
  }

  // ── Station 3 - one team average, typed in (record_mode 'average') ──────────

  function _renderAverageStation() {
    const teamId = _teamId();
    if (!teamId) return _missingTeamNotice();
    const ex = exerciseByKey(_queueExercise);
    const esc = UTILS.escapeHtml;
    const secs = ex?.template === 'stopwatch';
    document.getElementById('baseline-queue-title').textContent = ex?.name || '';
    document.getElementById('baseline-queue-sub').textContent = `${teamId} · ${t('bl.mode.average')}`;
    const el = document.getElementById('baseline-queue-content');
    if (!el) return;

    const rows = _rowsFor(_queueExercise, 'average');

    el.innerHTML = `
      <div style="margin-bottom:6px;"><button class="bl-illu-link" id="bl-how-measured">${esc(t('blillu.howMeasured'))}</button></div>
      <div class="sel-detail-hint" style="margin-bottom:10px;">${esc(t('bl.mode.avgHint'))}</div>
      <div class="gate__label">${esc(secs ? t('bl.mode.avgValueSecs') : t('bl.mode.avgValue'))}</div>
      <input class="field" id="bl-avg-value" type="number" inputmode="decimal" step="${secs ? '0.01' : '1'}" min="0" style="max-width:200px;">
      <div class="gate__label" style="margin-top:12px;">${esc(t('bl.mode.avgCount'))}</div>
      <input class="field" id="bl-avg-count" type="number" inputmode="numeric" step="1" min="1" style="max-width:200px;">
      <div class="sel-detail-hint">${esc(t('bl.mode.avgCountHint'))}</div>
      <div class="sel-detail-actions" style="margin-top:14px;">
        <button class="btn btn--primary" id="bl-avg-save">${esc(t('bl.mode.avgSave'))}</button>
      </div>
      ${rows.length ? `
        <div class="sel-detail-section__title" style="margin:16px 0 8px;">${esc(t('bl.tester.savedAttempts'))}</div>
        ${rows.map(r => `
          <div class="bl-queue-row">
            <span class="bl-queue-done"><strong>${esc(formatValue(ex, r))}</strong> · ${esc(t('bl.mode.avgOver', { count: r.subject_count }))}${r._pending ? ' ⏳' : ''}</span>
            ${r.id ? `<button class="sel-roster-btn sel-roster-btn--danger" data-del="${esc(r.id)}">🗑</button>` : ''}
          </div>`).join('')}` : ''}
    `;

    document.getElementById('bl-how-measured')
      ?.addEventListener('click', () => BASELINE_ILLUSTRATIONS.show(_queueExercise));
    document.getElementById('bl-avg-save')?.addEventListener('click', () => {
      const raw = Number(document.getElementById('bl-avg-value')?.value);
      const count = Math.round(Number(document.getElementById('bl-avg-count')?.value));
      if (!isFinite(raw) || raw <= 0 || !isFinite(count) || count < 1) {
        UI.toast(t('bl.mode.avgInvalid'), 'error');
        return;
      }
      // Stopwatch values are stored in ms; a coach types seconds.
      _saveTeamAverage(secs ? Math.round(raw * 1000) : raw, count);
    });
    el.querySelectorAll('[data-del]').forEach(b =>
      b.addEventListener('click', () => _confirmDelete(b.dataset.del)));
  }

  // Guard every removal the same way, so a mis-tap never wipes a real time.
  function _confirmDelete(id) {
    UI.modal({
      title: t('bl.tester.resetTitle'),
      body: t('bl.tester.resetBody'),
      buttons: [
        { label: t('common.cancel') },
        { label: t('bl.tester.resetConfirm'), style: 'danger', action: () => _deleteResult(id) },
      ],
    });
  }

  async function _deleteResult(id) {
    try {
      await DB.baseline.deleteResult(id);
      _results = _results.filter(r => String(r.id) !== String(id));
      _renderStation();
    } catch (err) {
      console.error('Delete result failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  function backToHome() { showHome(); }
  function backToQueue() { if (_queueExercise) showQueue(_queueExercise); else showHome(); }

  // The team an unattributed row belongs to. Missing means the session was
  // never scoped to a team, so there is nothing truthful to attach a time to.
  function _missingTeamNotice() {
    const el = document.getElementById('baseline-queue-content');
    const ex = exerciseByKey(_queueExercise);
    document.getElementById('baseline-queue-title').textContent = ex?.name || '';
    document.getElementById('baseline-queue-sub').textContent = _sname() || '';
    if (el) el.innerHTML = `<div class="sel-detail-hint">${UTILS.escapeHtml(t('bl.mode.noTeam'))}</div>`;
  }

  // ── Test screen - routes by template ─────────────────────────────────────────

  function _startTest(playerId) {
    const player = _players.find(p => p.id === playerId);
    const ex = exerciseByKey(_queueExercise);
    if (!player || !ex) return;
    UI.showScreen('screen-baseline-test');
    document.getElementById('baseline-test-title').textContent = player.name;
    document.getElementById('baseline-test-sub').textContent = ex.name;
    _live = { player, ex };
    if (ex.template === 'stopwatch')                              _renderStopwatch();
    else if (ex.template === 'reps')                             _renderReps();
    else                                                         _renderGates(); // gates / gates_dynamic
  }

  // The same templates, with nobody's name on them. `player` stays null, which
  // is what _saveAttempt reads to write a team row instead of a child's.
  function _startAnonTest() {
    const ex = exerciseByKey(_queueExercise);
    if (!ex || !_teamId()) return;
    UI.showScreen('screen-baseline-test');
    document.getElementById('baseline-test-title').textContent =
      t('bl.mode.anonNth', { n: _rowsFor(_queueExercise, 'anon').length + 1 });
    document.getElementById('baseline-test-sub').textContent = `${ex.name} · ${_teamId()}`;
    _live = { player: null, ex, anon: true };
    if (ex.template === 'stopwatch')   _renderStopwatch();
    else if (ex.template === 'reps')   _renderReps();
    else                               _renderGates();
  }

  function _testEl() { return document.getElementById('baseline-test-content'); }
  function _buzz(ms) { try { navigator.vibrate?.(ms); } catch { /* no haptics */ } }

  // ── Template A - Stopwatch ───────────────────────────────────────────────────

  function _renderStopwatch() {
    const esc = UTILS.escapeHtml;
    const st = _live;
    st.state = 'ready';        // ready | running | stopped
    st.startPerf = 0;
    st.elapsedMs = 0;
    st.ticker = null;
    st.laps = [];              // per-set splits (ms) when "Lap" is used
    st.lastLapPerf = 0;

    const setsLine = () => st.laps.length
      ? `<div class="sel-detail-hint" style="margin-top:6px;">${esc(t('bl.tester.sets'))}: ${st.laps.map(ms => (ms / 1000).toFixed(2)).join(' · ')} s</div>`
      : '';

    const paint = () => {
      const el = _testEl();
      el.innerHTML = `
        <div class="bl-clock">${(st.elapsedMs / 1000).toFixed(2)}<span class="bl-clock-unit">s</span></div>
        ${setsLine()}
        <div class="bl-test-controls">
          ${st.state === 'ready' ? `<button class="btn btn--primary bl-big" id="bl-start">${esc(t('bl.tester.start'))}</button>` : ''}
          ${st.state === 'running' ? `<button class="btn btn--primary bl-big" id="bl-stop">${esc(t('bl.tester.stop'))}</button>
            <button class="btn btn--secondary" id="bl-lap">${esc(t('bl.tester.lap'))}</button>` : ''}
          ${st.state === 'stopped' ? `
            <button class="btn btn--primary bl-big" id="bl-save">${esc(t('bl.tester.save'))}</button>
            <button class="btn btn--ghost" id="bl-discard">${esc(t('bl.tester.discard'))}</button>` : ''}
        </div>`;
      document.getElementById('bl-start')?.addEventListener('click', start);
      document.getElementById('bl-stop')?.addEventListener('click', stop);
      document.getElementById('bl-lap')?.addEventListener('click', lap);
      document.getElementById('bl-save')?.addEventListener('click', () =>
        _saveAttempt({ value: Math.round(st.elapsedMs), splits: st.laps.length ? st.laps.map(Math.round) : null }));
      document.getElementById('bl-discard')?.addEventListener('click', backToQueue);
    };
    const start = () => {
      st.state = 'running';
      st.startPerf = performance.now();
      st.lastLapPerf = st.startPerf;
      st.ticker = setInterval(() => {
        st.elapsedMs = performance.now() - st.startPerf;
        const c = _testEl()?.querySelector('.bl-clock');
        if (c) c.firstChild.nodeValue = (st.elapsedMs / 1000).toFixed(2);
      }, 50);
      _buzz(20);
      paint();
    };
    const lap = () => {
      const now = performance.now();
      st.laps.push(now - st.lastLapPerf);   // this set's time
      st.lastLapPerf = now;
      _buzz(20);
      paint();
    };
    const stop = () => {
      st.state = 'stopped';
      if (st.ticker) clearInterval(st.ticker);
      const now = performance.now();
      st.elapsedMs = now - st.startPerf;
      // If laps were used, the final segment is the last set.
      if (st.laps.length) st.laps.push(now - st.lastLapPerf);
      _buzz(40);
      paint();
    };
    paint();
  }

  // ── Template B - Rep counter (FitnessTimer countdown) ────────────────────────

  function _renderReps() {
    const esc = UTILS.escapeHtml;
    const st = _live;
    st.reps = 0;
    st.state = 'ready';        // ready | running | done
    const protocol = st.ex.protocol
      ? CONFIG.fitness.protocols[st.ex.protocol]
      : { mode: 'countdown', durationMs: st.ex.durationMs || 40000, beepAtSecondsLeft: [3, 2, 1, 0] };

    st.timer = FITNESS_TIMER.create({
      protocol,
      tickMs: 100,
      shouldRun: () => UI.currentScreen() === 'screen-baseline-test' && st.state === 'running',
      onTick: (ms) => { const c = _testEl()?.querySelector('.bl-clock'); if (c) c.firstChild.nodeValue = (ms / 1000).toFixed(1); },
      onFinish: () => { st.state = 'done'; _buzz([60, 40, 60]); paint(); },
    });

    const paint = () => {
      const el = _testEl();
      const showMs = st.state === 'ready' ? (protocol.durationMs || 40000) : st.timer.displayMs();
      el.innerHTML = `
        <div class="bl-clock">${(showMs / 1000).toFixed(1)}<span class="bl-clock-unit">s</span></div>
        ${st.state === 'running'
          ? `<button class="bl-rep-btn" id="bl-rep">${esc(t('bl.tester.tapRep'))}<span class="bl-rep-count">${st.reps}</span></button>`
          : `<div class="bl-rep-total">${st.reps} ${esc(t('bl.tester.reps'))}</div>`}
        <div class="bl-test-controls">
          ${st.state === 'ready' ? `<button class="btn btn--primary bl-big" id="bl-start">${esc(t('bl.tester.start'))}</button>` : ''}
          ${st.state === 'done' ? `
            <button class="btn btn--primary bl-big" id="bl-save">${esc(t('bl.tester.save'))}</button>
            <button class="btn btn--ghost" id="bl-discard">${esc(t('bl.tester.discard'))}</button>` : ''}
        </div>`;
      document.getElementById('bl-start')?.addEventListener('click', start);
      const rep = document.getElementById('bl-rep');
      if (rep) rep.addEventListener('click', () => {
        st.reps++; _buzz(15);
        const c = rep.querySelector('.bl-rep-count'); if (c) c.textContent = st.reps;
      });
      document.getElementById('bl-save')?.addEventListener('click', () => _saveAttempt({ value: st.reps }));
      document.getElementById('bl-discard')?.addEventListener('click', backToQueue);
    };
    const start = () => { st.state = 'running'; st.timer.start(); st.timer.startTicker(); _buzz(20); paint(); };
    paint();
  }

  // ── Template C - Gate scorer (3/1/0) ─────────────────────────────────────────

  function _renderGates() {
    const esc = UTILS.escapeHtml;
    const st = _live;
    st.taps = [];
    st.state = 'ready';        // ready | running | done
    const protocol = { mode: 'countdown', durationMs: st.ex.durationMs || 15000, beepAtSecondsLeft: [3, 2, 1, 0] };
    st.timer = FITNESS_TIMER.create({
      protocol,
      tickMs: 100,
      shouldRun: () => UI.currentScreen() === 'screen-baseline-test' && st.state === 'running',
      onTick: (ms) => { const c = _testEl()?.querySelector('.bl-clock'); if (c) c.firstChild.nodeValue = (ms / 1000).toFixed(1); },
      onFinish: () => finish(),
    });

    const finish = () => { if (st.state !== 'running') return; st.state = 'done'; st.timer.stopTicker(); _buzz([60, 40, 60]); paint(); };

    const paint = () => {
      const el = _testEl();
      const g = gateScore(st.taps, st.ex);
      const showMs = st.state === 'ready' ? (st.ex.durationMs || 15000) : st.timer.displayMs();
      const phaseLabel = st.ex.template === 'gates_dynamic'
        ? `${g.phase === 'push' ? t('bl.tester.push') : t('bl.tester.shot')} ${g.phaseBall > g.phaseTotalBalls ? g.phaseTotalBalls : g.phaseBall}/${g.phaseTotalBalls}`
        : `${esc(t('bl.tester.ball'))} ${Math.min(g.phaseBall, g.phaseTotalBalls || g.phaseBall)}${g.phaseTotalBalls ? '/' + g.phaseTotalBalls : ''}`;

      el.innerHTML = `
        <div class="bl-clock">${(showMs / 1000).toFixed(1)}<span class="bl-clock-unit">s</span></div>
        <div class="bl-gate-status"><span class="bl-gate-total">${g.total}</span> ${esc(phaseLabel)}</div>
        ${st.state === 'running' ? `
          <div class="bl-gate-buttons">
            <button class="bl-gate-btn bl-gate-3" data-pts="3">3</button>
            <button class="bl-gate-btn bl-gate-1" data-pts="1">1</button>
            <button class="bl-gate-btn bl-gate-0" data-pts="0">0</button>
          </div>
          <button class="btn btn--ghost" id="bl-undo">${esc(t('bl.tester.undo'))}</button>` : ''}
        <div class="bl-test-controls">
          ${st.state === 'ready' ? `<button class="btn btn--primary bl-big" id="bl-start">${esc(t('bl.tester.start'))}</button>` : ''}
          ${st.state === 'done' ? `
            <button class="btn btn--primary bl-big" id="bl-save">${esc(t('bl.tester.save'))}</button>
            <button class="btn btn--ghost" id="bl-discard">${esc(t('bl.tester.discard'))}</button>` : ''}
        </div>`;

      document.getElementById('bl-start')?.addEventListener('click', start);
      el.querySelectorAll('[data-pts]').forEach(b =>
        b.addEventListener('click', () => {
          st.taps.push(Number(b.dataset.pts));
          _buzz(15);
          if (gateScore(st.taps, st.ex).done) finish(); else paint();
        }));
      document.getElementById('bl-undo')?.addEventListener('click', () => { st.taps.pop(); paint(); });
      document.getElementById('bl-save')?.addEventListener('click', () => {
        const g = gateScore(st.taps, st.ex);
        _saveAttempt({ value: g.total, value_push: g.valuePush, value_shot: g.valueShot });
      });
      document.getElementById('bl-discard')?.addEventListener('click', backToQueue);
    };
    const start = () => { st.state = 'running'; st.timer.start(); st.timer.startTicker(); _buzz(20); paint(); };
    paint();
  }

  // ── Save (with localStorage offline queue) ───────────────────────────────────

  async function _saveAttempt(values) {
    // An unattributed attempt belongs to the team; a normal one to the child
    // (or the guest). The CHECK constraint on baseline_results refuses any
    // other combination, so these three shapes are the only valid ones.
    const subject = _live.anon
      ? { player_id: null, external_id: null, team_id: _teamId(), record_mode: 'anon' }
      : {
          player_id:   _live.player.external ? null : _live.player.id,
          external_id: _live.player.external ? _live.player.id : null,
          team_id:     null,
          record_mode: 'player',
        };
    const payload = {
      session_id:     _sid(),
      ...subject,
      exercise_key:   _live.ex.key,
      value:          values.value ?? null,
      value_push:     values.value_push ?? null,
      value_shot:     values.value_shot ?? null,
      splits:         values.splits ?? null,
      tester_code_id: _codeId(),
    };
    try {
      await DB.baseline.recordResult(payload);
      await _reloadResults();
      UI.toast(t('bl.tester.saved'), 'success', 1200);
    } catch (err) {
      // Offline / transient - queue it and show optimistically.
      console.error('Result save queued (save failed live):', err);
      _queuePending(payload);
      _results.push({ ...payload, _pending: true });
      UI.toast(t('bl.tester.savedOffline'), 'info', 1800);
    }
    backToQueue();
  }

  // Mode 3. subject_count is required by the constraint, because an average of
  // 3 girls must not weigh the same as an average of 18 when the age group is
  // rolled up.
  async function _saveTeamAverage(value, subjectCount) {
    const payload = {
      session_id:     _sid(),
      player_id:      null,
      external_id:    null,
      team_id:        _teamId(),
      record_mode:    'average',
      subject_count:  subjectCount,
      exercise_key:   _queueExercise,
      value,
      tester_code_id: _codeId(),
    };
    try {
      await DB.baseline.recordResult(payload);
      await _reloadResults();
      UI.toast(t('bl.tester.saved'), 'success', 1200);
    } catch (err) {
      console.error('Team average save queued (save failed live):', err);
      _queuePending(payload);
      _results.push({ ...payload, _pending: true });
      UI.toast(t('bl.tester.savedOffline'), 'info', 1800);
    }
    _renderStation();
  }

  async function _reloadResults() {
    try { _results = await DB.baseline.listResults(_sid()) || []; }
    catch { /* keep what we have */ }
  }

  function _loadPending() {
    try { return JSON.parse(localStorage.getItem(_pendingKey()) || '[]'); }
    catch { return []; }
  }
  function _savePending(arr) {
    try { localStorage.setItem(_pendingKey(), JSON.stringify(arr)); } catch { /* full/unavailable */ }
  }
  function _queuePending(payload) {
    const arr = _loadPending(); arr.push(payload); _savePending(arr);
  }

  async function _flushPending() {
    const arr = _loadPending();
    if (!arr.length) return;
    const remaining = [];
    for (const p of arr) {
      try { await DB.baseline.recordResult(p); }
      catch { remaining.push(p); }
    }
    _savePending(remaining);
    if (remaining.length < arr.length) await _reloadResults();
  }

  function _startFlusher() {
    if (_flushTimer) return;
    _flushTimer = setInterval(() => {
      if (!_active()) { clearInterval(_flushTimer); _flushTimer = null; return; }
      if (_loadPending().length) _flushPending().then(() => {
        if (UI.currentScreen() === 'screen-baseline-tester') _renderHome();
      });
    }, 5000);
  }

  // A coach exits back to the Coach Hub instead of signing out (BT- helpers
  // sign out). Returns true if it handled the exit. Called by the shared
  // sign-out button so app.js needs no tester internals.
  function exitToHubIfStaff() {
    if (!_ctx) return false;
    const onExit = _ctx.onExit;
    if (_flushTimer) { clearInterval(_flushTimer); _flushTimer = null; }
    _ctx = null;
    if (onExit) onExit();
    return true;
  }

  return { showHome, backToHome, backToQueue, exitToHubIfStaff };

})();
